import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import gift from '../../assets/wallet-management/deposit_gift.webp'
import classNames from '../../utils/classNames'
import { numberWithCommas } from '../../utils/number'
import Accordion from '../common/accordion'
import type { Item } from '../common/scroll-selector'
import Icon from '../icon'
import { TextInput } from '../register'
import type { PromoType } from '../wallet-management/deposit-form'

export type Promo = Item & {
  content: string
  bonus_amount: number
  total_credit_amount: number
}

interface PromotionInputProps {
  selectedPromo: Promo | null
  onPromoSelect: (promo: Promo) => void
  customPromoCode: string
  onCustomPromoChange: (promo: string) => void
  promoList: Promo[]
  showPromoCode: boolean
  promoCodeError: string | null
  promoType: PromoType
  setPromoType: (type: PromoType) => void
  amount: number
}

const Amount = ({ children }: { children: ReactNode }) => (
  <p className="font-bold uppercase lg:text-lg text-secondary-txt [&_span]:text-highlight dark:[&_span]:text-highlight-gold">
    {children}
  </p>
)

const PromotionInput = ({
  selectedPromo,
  promoList,
  onPromoSelect,
  customPromoCode,
  onCustomPromoChange,
  showPromoCode,
  promoCodeError,
  promoType,
  setPromoType,
  amount,
}: PromotionInputProps) => {
  const { t } = useTranslation('walletManagement')
  const showAutoPromotion = promoList.length > 0

  return (
    <div className="border-t border-tertiary">
      <div
        className="flx-str-ctr mt-4 mb-4"
        style={{
          flexWrap: 'wrap',
          gap: 12,
          justifyContent: 'space-between',
        }}>
        <Amount>
          {t('bonus')}:{' '}
          <span>
            $
            {numberWithCommas(
              selectedPromo?.total_credit_amount
                ? +selectedPromo?.bonus_amount
                : 0,
            )}
          </span>
        </Amount>
        <Amount>
          {t('total-credits')}:{' '}
          <span>${numberWithCommas(amount ? +amount : 0)}</span>
        </Amount>
      </div>
      <div className="flex flex-col items-start gap-4">
        {showAutoPromotion && (
          <div className="w-full">
            <div className="flex items-center gap-2 mb-2">
              <p className="deposit-section-title font-semibold uppercase text-sm text-txt xl:text-base dark:text-[#f6d56c] dark:font-bold relative">
                {t('bonus-available')}
              </p>
            </div>
            <div className="promo-list-container flex gap-3 overflow-x-auto py-1">
              {promoList.map(promo => (
                <button
                  key={promo.id}
                  disabled={!!customPromoCode}
                  className={classNames(
                    selectedPromo?.id && +selectedPromo.id === +promo.id
                      ? 'deposit-bonus-active bg-highlight dark:bg-highlight-bg shadow-[0px_0px_5px_2px_rgba(0,166,255,0.5)] dark:!bg-transparent dark:!border-[#f5c542] dark:!shadow-[0_0_16px_rgba(245,197,66,0.14)]'
                      : 'bg-tertiary dark:bg-[#252525]',
                    'min-w-[40%] max-w-[40%] h-[55px] flex items-center relative px-2 py-2 rounded-lg shrink-0 xl:min-w-[160px] xl:max-w-[160px] xl:h-[70px] xl:px-3 xl:py-4 dark:rounded-lg dark:border dark:border-solid dark:border-[rgba(255,255,255,0.08)] dark:transition-all dark:duration-200 disabled:opacity-50 disabled:cursor-not-allowed',
                  )}
                  onClick={() => {
                    if (selectedPromo?.id && +selectedPromo.id === +promo.id) {
                      setPromoType(null)
                      onPromoSelect({ label: '', id: 0, content: '', bonus_amount: 0, total_credit_amount: 0 })
                    } else {
                      setPromoType('autopromo')
                      onCustomPromoChange('')
                      onPromoSelect(promo)
                    }
                  }}>
                  <p
                    className={classNames(
                      selectedPromo?.id && +selectedPromo.id === +promo.id
                        ? 'text-white'
                        : 'text-txt',
                      'promo-name text-xs font-bold w-full overflow-hidden text-ellipsis text-left xl:text-sm',
                    )}>
                    {promo.label}
                  </p>
                  <img
                    className={classNames(
                      selectedPromo?.id && +selectedPromo.id === +promo.id
                        ? 'opacity-100'
                        : 'opacity-60',
                      'w-6 h-6 ml-2 xl:w-8 xl:h-8 xl:ml-3 shrink-0',
                    )}
                    src={gift}
                    alt="gift"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
        {showPromoCode && (
          <div className="w-full">
          <Accordion label={t('enter-promo-code')}>
            <TextInput
              value={customPromoCode}
              onChange={e => onCustomPromoChange(e.currentTarget.value)}
              containerClassName="!mb-0 w-full bg-primary dark:bg-[#0f1012] h-[45px] lg:h-[50px] text-txt text-left px-3 font-Avenir-Light rounded-[5px]"
              className="!text-base disabled:opacity-70 disabled:cursor-not-allowed"
              disabled={promoType === 'autopromo'}
              placeholder={t('enter-promo-code')}
            />
            {promoCodeError && (
              <div className="bg-red-500 px-3 text-white py-1 rounded-sm text-sm mt-2 flex gap-2 items-center">
                <Icon id="warning" width="16" height="16" className="fill-red-500" />
                {promoCodeError}
              </div>
            )}
          </Accordion>
          </div>
        )}
      </div>
    </div>
  )
}

export default PromotionInput
